import React from 'react';
import { useSelector } from 'react-redux';
import { Menu, MenuTrigger, MenuPopover, MenuList, MenuDivider } from '@fluentui/react-components';
import styles from './css/index.module.css';
import { isReservedOffTrackTag } from '../../utils/taskSchedulingUtils';

/**
 * TagFilterMenu
 *
 * A multi-select tag picker for the Task Management filter bar.
 * - Uses project-level tags from Redux (`projectTags`)
 * - Also derives tags from `taskData` (`task.tags` or legacy `tag_repr`)
 * - Clicking a tag toggles it in the selected list.
 */
const TagFilterMenu = ({ value, onChange, taskData }) => {
    const { projectTags } = useSelector(state => state.task);
    const [open, setOpen] = React.useState(false);

    const selectedTags = React.useMemo(() => {
        if (Array.isArray(value)) return value;
        if (!value) return [];
        return [value];
    }, [value]);

    const allTags = React.useMemo(() => {
        const tagMap = new Map();

        // Project-level tags
        if (projectTags && projectTags.length > 0) {
            projectTags.forEach(tag => {
                if (tag?.name && !isReservedOffTrackTag(tag.name)) {
                    tagMap.set(tag.name, { id: tag.id || `project_${tag.name}`, name: tag.name, source: 'project' });
                }
            });
        }

        // Tags derived from task data
        const collectTasks = () => {
            if (Array.isArray(taskData)) return taskData;
            if (taskData && Array.isArray(taskData.data)) return taskData.data;
            if (taskData && Array.isArray(taskData.groups)) {
                return taskData.groups.flatMap(group => group.tasks || []);
            }
            return [];
        };

        const tasks = collectTasks();
        tasks.forEach(task => {
            const taskTags = Array.isArray(task.tags)
                ? task.tags
                : task?.tag_repr
                ? [task.tag_repr]
                : [];
            taskTags.forEach(tagName => {
                if (tagName && tagName.trim() !== '' && !isReservedOffTrackTag(tagName) && !tagMap.has(tagName)) {
                    tagMap.set(tagName, {
                        id: `task_${tagName}`,
                        name: tagName,
                        source: 'task',
                    });
                }
            });
        });

        return Array.from(tagMap.values())
            .filter(tag => !isReservedOffTrackTag(tag.name))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [projectTags, taskData]);

    const toggleTag = tagName => {
        let next;
        if (selectedTags.includes(tagName)) {
            next = selectedTags.filter(t => t !== tagName);
        } else {
            next = [...selectedTags, tagName];
        }
        onChange(next);
    };

    const handleClear = () => {
        onChange([]);
    };

    const triggerLabel =
        selectedTags.length === 0
            ? 'Any tag'
            : selectedTags.length === 1
            ? selectedTags[0]
            : `${selectedTags.length} tags selected`;

    return (
        <Menu open={open} onOpenChange={(e, data) => setOpen(data.open)}>
            <MenuTrigger>
                <button
                    type="button"
                    className={'form-select form-select-sm ' + styles.filterSelect}
                    style={{
                        textAlign: 'left',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 4,
                        cursor: 'pointer',
                    }}>
                    <span
                        className={styles.tableRegularText}
                        style={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            flex: 1,
                        }}>
                        {triggerLabel}
                    </span>
                </button>
            </MenuTrigger>
            <MenuPopover style={{ width: 232, maxHeight: 400, overflowY: 'auto' }}>
                <MenuList>
                    {allTags.length > 0 ? (
                        <>
                            {allTags.map(tag => {
                                const isSelected = selectedTags.includes(tag.name);
                                return (
                                    <div
                                        key={tag.id}
                                        onClick={() => toggleTag(tag.name)}
                                        className={styles.tagMenuItem}
                                        style={
                                            isSelected
                                                ? {
                                                      backgroundColor: 'rgba(200, 151, 65, 0.05)',
                                                  }
                                                : undefined
                                        }>
                                        <span>{tag.name}</span>
                                    </div>
                                );
                            })}
                        </>
                    ) : (
                        <div style={{ padding: '8px 12px' }}>
                            <p className="mb-0">No tags available</p>
                        </div>
                    )}

                    {selectedTags.length > 0 && (
                        <>
                            <MenuDivider />
                            <div onClick={handleClear} className={styles.tagMenuItem}>
                                <span>Clear selected tags</span>
                            </div>
                        </>
                    )}
                </MenuList>
            </MenuPopover>
        </Menu>
    );
};

export default TagFilterMenu;

